<template>
	<validation-provider
		:rules="rules"
		:name="name"
		v-bind="$attrs"
		v-slot="{ errors, validate }"
	>
		<b-form-group label-class="d-flex align-items-center">
			<template #label>
				{{ label }}
				<b-icon
					v-if="isLoading"
					icon="circle-fill"
					animation="fade"
					font-scale="0.5"
					class="mt-1 ml-2"
				></b-icon>
			</template>

			<b-dropdown
				ref="dropdown"
				@hidden="validate"
				:disabled="disabled"
				:text="selectedOption"
				class="form-control"
				:dropup="'top' === dropdownPosition ? true : false"
				:dropright="'right' === dropdownPosition ? true : false"
				:dropleft="'left' === dropdownPosition ? true : false"
				block
				lazy
			>
				<input type="hidden" v-model="innerValue" />
				<div key="tab-list">
					<b-dropdown-text class="text-right">
						<b-form-input
							@input="onSearch"
							:placeholder="`Search ${label}`"
							size="sm"
						></b-form-input>

						<a
							v-if="multiple"
							@click.prevent="selectAll"
							class="mt-2 d-block small"
							href
							>{{
								innerValue && innerValue.length
									? "Unselect"
									: "Select"
							}}
							All</a
						>
					</b-dropdown-text>
					<b-dropdown-divider></b-dropdown-divider>
					<template v-if="0 < dropdownOptions.length">
						<b-dropdown-item-button
							v-for="dropdownOption in dropdownOptions"
							@click.native.capture.stop="
								onSelect(dropdownOption.value)
							"
							:key="`option-${dropdownOption.value}`"
							:active="isSelected(dropdownOption.value)"
							active-class="bg-light"
						>
							{{ dropdownOption.text }}
						</b-dropdown-item-button>
					</template>
					<small v-else class="d-block py-2 px-3"
						>No {{ label }} found.</small
					>
				</div>
			</b-dropdown>
			<slot name="error">
				<div
					v-if="errors[0]"
					class="invalid-feedback"
					style="display: block"
				>
					{{ errors[0] }}
				</div>
			</slot>
		</b-form-group>
	</validation-provider>
</template>

<script>
import Fuse from "fuse.js";

export default {
	name: "searchable-dropdown",
	props: {
		disabled: Boolean,
		isLoading: Boolean,
		multiple: Boolean,
		label: {
			type: String,
			description: "Input label",
		},
		name: {
			type: String,
			description: "Input name",
		},
		options: {
			type: Array,
			description: "Option dropdown items",
			required: true,
		},
		rules: {
			type: [String, Array],
			description: "Validation rules",
		},
		value: {
			type: [String, Number, Array],
			description: "Input value",
		},
		dropdownPosition: {
			type: String,
			description: "Dropdown options position. (top|right|bottom|left)",
			validator: function (value) {
				return 0 <= ["top", "right", "bottom", "left"].indexOf(value);
			},
		},
	},
	data() {
		return {
			searchQuery: undefined,
			innerValue: undefined,
		};
	},
	computed: {
		dropdownOptions() {
			let options = this.options;
			const fuse = new Fuse(options, {
				keys: ["text", "value"],
				includeScore: true,
				useExtendedSearch: true,
				threshold: 0.3,
			});

			if (this.searchQuery)
				options = fuse
					.search(`'${this.searchQuery}`)
					.map((item) => item.item);

			return _.orderBy(options, "text", "asc");
		},
		selectedOption() {
			if (!this.dropdownOptions.length || !this.innerValue)
				return `Select ${this.label}`;

			if (this.multiple) {
				return this.innerValue
					? this.innerValue.join(",")
					: `Select ${this.label}`;
			} else {
				const idx = this.dropdownOptions.findIndex(
					(item) => item.value == this.innerValue
				);

				return 0 <= idx
					? this.dropdownOptions[idx].text
					: `Select ${this.label}`;
			}
		},
	},
	watch: {
		value(newVal) {
			if (!newVal) this.innerValue = newVal;
		},
		innerValue(newVal) {
			this.$emit("input", newVal);
		},
		dropdownOptions() {
			this.innerValue = undefined;
		},
	},
	methods: {
		isSelected(value) {
			if (this.multiple) {
				return this.innerValue
					? 0 <= this.innerValue.indexOf(value)
					: false;
			} else {
				return this.innerValue == value;
			}
		},
		onSearch: _.debounce(function (keyword) {
			this.searchQuery = keyword;
		}, 500),
		onSelect(value) {
			if (this.multiple) {
				this.innerValue = this.innerValue || [];

				const idx = this.innerValue.indexOf(value);

				if (0 <= idx) this.innerValue.splice(idx, 1);
				else this.innerValue.push(value);
			} else {
				this.innerValue = value;

				this.$refs.dropdown.hide(true);
			}

			this.$emit("select", this.innerValue);
		},
		selectAll() {
			if (!this.multiple) return false;

			this.innerValue = this.innerValue
				? undefined
				: this.options.map((item) => item.value);
		},
	},
	created() {
		if (this.value) this.onSelect(this.value);
	},
};
</script>

<style lang="scss">
.dropdown-toggle {
	text-overflow: ellipsis;
	overflow: hidden;
}
</style>
